import React, { useEffect } from "react";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { Lock, Mail, Database, ShieldCheck } from "lucide-react";

const SECTIONS = [
  {
    id: "collect",
    title: "Information We Collect",
    body: (
      <>
        <p>To run the IdeaThon platform, we collect the following:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><span className="text-white">Personal details</span> — your name, personal email, college email, and college name. Your <span className="text-white">phone / WhatsApp number is required</span> for account verification, payment support, and important IdeaThon updates. LinkedIn profile URL is optional.</li>
          <li><span className="text-white">Payment information</span> — processed securely via <span className="text-white">Razorpay</span>. NoobSync Innovate does <span className="text-brand">not store</span> your card number, CVV, UPI PIN, or any sensitive payment credential.</li>
          <li><span className="text-white">Submission content</span> — your project details, video links, repository links, and demo links submitted in Round 1 and Round 2.</li>
          <li><span className="text-white">Communication</span> — emails we send you via <span className="text-white">Brevo</span> for verification, notifications, and competition updates.</li>
        </ul>
      </>
    ),
  },
  {
    id: "use",
    title: "How We Use Your Information",
    body: (
      <ul className="list-disc pl-5 space-y-1.5">
        <li>To verify your eligibility via institutional email verification (OTP + magic link).</li>
        <li>To process your registration payment.</li>
        <li>To facilitate team formation, invitations, and team-level communication.</li>
        <li>To enable submissions and judging across both rounds.</li>
        <li>To send important updates about the competition, deadlines, and results.</li>
        <li>To showcase winning submissions for recognition and promotional purposes, in accordance with the IP clause in our Terms &amp; Conditions.</li>
      </ul>
    ),
  },
  {
    id: "third-party",
    title: "Third-Party Services",
    body: (
      <>
        <p>To operate this platform, we share the minimum necessary data with the following processors:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><span className="text-white">Razorpay</span> — secure payment processing (PCI-DSS compliant). We never store your card number, CVV, or UPI PIN.</li>
          <li><span className="text-white">Brevo</span> — transactional email delivery (verification &amp; notifications).</li>
          <li><span className="text-white">Google Analytics 4</span> (Google LLC) — aggregate website traffic and usage analytics.</li>
          <li><span className="text-white">Microsoft Clarity</span> (Microsoft Corp.) — anonymised usage analytics and interaction heatmaps.</li>
          <li><span className="text-white">Meta Pixel &amp; Conversions API</span> (Meta Platforms, Inc.) — advertising measurement for our campaigns.</li>
        </ul>
        <p>Google Analytics 4, Microsoft Clarity and the Meta Pixel are non-essential and load only after you choose &ldquo;Accept&rdquo; in our cookie banner. If you &ldquo;Decline,&rdquo; these are not loaded and you can still register, pay, form a team, and participate fully. Any identifiers shared with Meta (such as email) are cryptographically hashed before transmission.</p>
        <p><span className="text-white">NoobSync HI marketing</span> is entirely separate and optional: only if you tick the dedicated consent box at registration will we contact you about NoobSync HI courses, events, internships, and offers. You can opt out at any time; it is never required to join IdeaThon.</p>
        <p>We <span className="text-brand">do not sell, rent, or share</span> your personal information with any third party for their own marketing purposes.</p>
      </>
    ),
  },
  {
    id: "security",
    title: "Data Security",
    body: (
      <>
        <p>Your information is stored on secured servers, with access restricted to authorised NoobSync Innovate administrators only. Passwords (where applicable) are hashed. Verification tokens are time-limited and single-use.</p>
        <p>We take reasonable industry-standard measures to protect your data from unauthorised access, loss, or misuse. However, no system on the internet can be guaranteed 100% secure; you participate at your own awareness of this risk.</p>
      </>
    ),
  },
  {
    id: "rights",
    title: "Your Rights",
    body: (
      <>
        <p>Under the <span className="text-white">Digital Personal Data Protection Act, 2023</span>, you have the right to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><span className="text-white">Access</span> the personal data we hold about you.</li>
          <li><span className="text-white">Correct</span> any inaccurate or incomplete data.</li>
          <li><span className="text-white">Request deletion</span> of your personal data (subject to legal record-keeping obligations).</li>
          <li><span className="text-white">Withdraw consent</span> to non-essential communications at any time.</li>
        </ul>
        <p>To exercise any of these rights, email us at <a href="mailto:info@noobsync.com" className="text-brand hover:underline">info@noobsync.com</a> from your registered email address.</p>
      </>
    ),
  },
  {
    id: "retention",
    title: "Data Retention",
    body: (
      <>
        <p>We retain your information for the duration of the competition and for a reasonable period afterward for the following purposes:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Record-keeping and audit compliance.</li>
          <li>Results archival and the public &quot;Series&quot; page.</li>
          <li>Future communication about NoobSync Innovate editions, unless you opt out.</li>
        </ul>
        <p>You may request earlier deletion of your account via <a href="mailto:info@noobsync.com" className="text-brand hover:underline">info@noobsync.com</a>.</p>
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies & Local Storage",
    body: (
      <>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><span className="text-white">Essential (always on):</span> a single secure, httpOnly session cookie that keeps you signed in (a short-lived token JavaScript cannot read), plus minimal browser storage to remember your team context and your cookie choice. These are required for the site to work and are not used for advertising.</li>
          <li><span className="text-white">Analytics &amp; advertising (only after you Accept):</span> Google Analytics 4 and Microsoft Clarity help us understand aggregate traffic and page usage (page views, referral source, approximate country/region, device type, anonymised heatmaps); the Meta Pixel measures our ad campaigns. These set their own cookies and run only with your consent.</li>
        </ul>
        <p>You can change your mind at any time by clearing cookies in your browser, and you can block cookies entirely via your browser settings. We do <span className="text-brand">not</span> sell your data.</p>
      </>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <>
        <p>For any privacy-related questions, data requests, or concerns, contact us at <a href="mailto:info@noobsync.com" className="text-brand hover:underline">info@noobsync.com</a>. We aim to respond within 7 business days.</p>
      </>
    ),
  },
];

export default function Privacy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="App" data-testid="privacy-page">
      <Navbar />

      <section className="relative grid-bg border-b border-white/10">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-20">
          <div className="label-eyebrow mb-4">
            Last updated: 19 July 2026 · Effective 2026
          </div>
          <h1 className="font-display text-5xl sm:text-7xl text-white leading-[0.95]" data-testid="privacy-title">
            Privacy Policy
          </h1>
          <p className="mt-6 text-gray-400 max-w-2xl text-base sm:text-lg leading-relaxed">
            What we collect, how we use it, and the control you have over your data — written plainly, no dark patterns.
          </p>
        </div>
      </section>

      <section className="bg-[#080C18]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-12 py-14 grid lg:grid-cols-[260px_1fr] gap-10">
          {/* TOC */}
          <aside className="lg:sticky lg:top-20 lg:self-start" data-testid="privacy-toc">
            <div className="label-eyebrow mb-3">On this page</div>
            <ul className="space-y-2 text-sm">
              {SECTIONS.map((s, i) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-gray-400 hover:text-brand transition-colors flex gap-2" data-testid={`toc-${s.id}`}>
                    <span className="text-gray-600 font-mono">{String(i + 1).padStart(2, "0")}</span> {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </aside>

          {/* Body */}
          <div className="space-y-10">
            <div className="card-surface border-l-2 border-brand flex items-start gap-3" data-testid="privacy-banner">
              <ShieldCheck className="w-5 h-5 text-brand mt-0.5 flex-shrink-0" strokeWidth={2} />
              <p className="text-sm text-gray-300 leading-relaxed">
                <span className="text-white font-semibold">In one line:</span> we collect only what we need to run the hackathon, never sell your data, and let you delete your account by emailing us.
              </p>
            </div>

            {SECTIONS.map((s, i) => (
              <article id={s.id} key={s.id} className="scroll-mt-24" data-testid={`section-${s.id}`}>
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="font-mono text-xs text-brand">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-display text-2xl sm:text-3xl text-white">{s.title}</h2>
                </div>
                <div className="text-gray-400 leading-relaxed space-y-3 pl-0 sm:pl-9 text-[15px]">
                  {s.body}
                </div>
              </article>
            ))}

            <div className="card-surface flex items-start gap-3 mt-12" data-testid="privacy-contact">
              <Lock className="w-5 h-5 text-brand mt-0.5 flex-shrink-0" strokeWidth={2} />
              <div className="text-sm">
                <div className="text-white font-semibold mb-1">Privacy concerns?</div>
                <p className="text-gray-400">
                  Email us at{" "}
                  <a href="mailto:info@noobsync.com" className="text-brand hover:underline inline-flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5" /> info@noobsync.com
                  </a>
                  {" "}and we&apos;ll respond within 7 business days.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
